import { supabase, getActiveOrgOverride, DEFAULT_ORG_ID, PLATFORM_CONFIG_ID } from '../supabaseClient'
import { getCurrentStaff } from './auth'
import { LEGAL_DOC_KEYS, getLegalDocDefinition, REQUIRED_ORG_LEGAL_DOCS } from '../../legal/documents'
import { renderDocRequerido } from '../../legal/render'

export { LEGAL_DOC_KEYS, REQUIRED_ORG_LEGAL_DOCS }

// El render de los documentos es PURO y vive en src/legal/render.js (sin Supabase), para que
// Torneos lo copie tal cual. Aqui se reexporta con los mismos nombres: ningun import cambia.
export {
  renderLegalDoc,
  renderAvisoClub,
  renderAvisoMyklubb,
  renderDpa,
  renderDeclaracionClubSinMyklubb,
  renderTosSaas,
  renderDocRequerido,
} from '../../legal/render'

// Lee la entidad legal de MYKLUBB (placeholders configurables).
export const getPlatformLegalEntity = async () => {
  const { data, error } = await supabase
    .from('platform_config')
    .select('legal_entity')
    .eq('id', PLATFORM_CONFIG_ID)
    .single()
  return { data: data?.legal_entity || {}, error }
}

// Registra la aceptación de un documento legal (click-through). Versión, usuario, fecha.
export const recordLegalAcceptance = async (docKey, { orgId = null, ip = null, userAgent = null, documentoDatos = null, textoMostrado = null } = {}) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const resolvedOrgId = getActiveOrgOverride() || orgId || staff?.org_id || DEFAULT_ORG_ID
    const def = getLegalDocDefinition(docKey)

    const { data, error } = await supabase
      .from('org_legal_acceptances')
      .insert({
        org_id: resolvedOrgId,
        doc_key: docKey,
        doc_version: def ? def.version : 'desconocida',
        accepted_by: staff?.id || null,
        ip,
        user_agent: userAgent || (typeof navigator !== 'undefined' ? navigator.userAgent : null),
        ...(documentoDatos ? { documento_datos: documentoDatos } : {}),
        ...(textoMostrado ? { texto_mostrado: textoMostrado } : {}),
      })
      .select()
      .single()

    if (error) throw error
    return { data, error: null }
  } catch (error) {
    console.error('[recordLegalAcceptance]', error)
    return { data: null, error }
  }
}

// Compat: aceptación del DPA.
export const recordDpaAcceptance = (opts = {}) => recordLegalAcceptance(LEGAL_DOC_KEYS.DPA, opts)

// Hasta la pieza 2b, encender una casilla de comparticion registraba una aceptacion del DPA
// con `documento_datos.motivo = 'casilla'`: una ENMIENDA, no la aceptacion base que da el
// titular en la puerta legal. Ya no se crean (spec 2026-10-11 §6), pero las filas viejas se
// conservan: no cuentan como "el DPA aceptado" en la puerta, en hasAcceptedLegal ni en el
// expediente (si contaran, quien encendio una casilla habria aceptado el DPA por el club).
const esEnmiendaCasilla = (fila) => fila?.documento_datos?.motivo === 'casilla'

// ¿La org ya aceptó la versión vigente de un documento? (Las enmiendas por casilla no cuentan.)
export const hasAcceptedLegal = async (docKey, orgId = null) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const resolvedOrgId = getActiveOrgOverride() || orgId || staff?.org_id || DEFAULT_ORG_ID
    const def = getLegalDocDefinition(docKey)

    // Sin limit(1): la primera fila podría ser una enmienda y tapar la aceptación base.
    const { data, error } = await supabase
      .from('org_legal_acceptances')
      .select('id, documento_datos')
      .eq('org_id', resolvedOrgId)
      .eq('doc_key', docKey)
      .eq('doc_version', def ? def.version : '')
      .is('invalidada_at', null)

    if (error) throw error
    return { accepted: (data || []).some((f) => !esEnmiendaCasilla(f)), error: null }
  } catch (error) {
    console.error('[hasAcceptedLegal]', error)
    return { accepted: false, error }
  }
}

export const hasAcceptedCurrentDpa = (orgId = null) => hasAcceptedLegal(LEGAL_DOC_KEYS.DPA, orgId)

// Expediente legal del club: qué aceptó, en qué versión, quién y cuándo.
//
// El sistema guardaba todo esto desde el principio y no había dónde verlo. Un club
// que firma un contrato tiene derecho a consultar qué firmó sin pedírselo a nadie
// -- y si llega una queja o una autoridad, ese registro es la respuesta.
//
// Devuelve una fila por documento requerido, aceptado o no, para que la pantalla
// pueda mostrar también lo que falta. La aceptación que cuenta es la base (la última
// que no es enmienda por casilla); las enmiendas van aparte, en `enmiendas`.
export const getExpedienteLegal = async (orgId = null) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const resolvedOrgId = getActiveOrgOverride() || orgId || staff?.org_id || DEFAULT_ORG_ID
    if (!resolvedOrgId) return { data: [], error: null }

    const { data: filas, error } = await supabase
      .from('org_legal_acceptances')
      .select('doc_key, doc_version, accepted_by, accepted_at, created_at, invalidada_at, invalidada_motivo, documento_datos')
      .eq('org_id', resolvedOrgId)
      .order('created_at', { ascending: false })
    if (error) throw error

    // Los nombres se resuelven aparte: `accepted_by` es un id de staff y en la UI
    // jamás se muestra un id (regla de UX del proyecto).
    const ids = [...new Set((filas || []).map((f) => f.accepted_by).filter(Boolean))]
    const porId = {}
    if (ids.length) {
      const { data: personas } = await supabase
        .from('staff')
        .select('id, nombre_completo, email')
        .in('id', ids)
      ;(personas || []).forEach((p) => { porId[p.id] = p })
    }

    const nombreDe = (staffId) => porId[staffId]?.nombre_completo || porId[staffId]?.email || null

    // La primera de cada documento es la más reciente (vienen ordenadas desc).
    // Para el estado cuenta la ultima aceptacion VIGENTE que no es enmienda por casilla;
    // las invalidadas se conservan y se muestran aparte, porque explican por que hubo
    // que rehacerlo. Las enmiendas tambien van aparte: no son la aceptacion del titular.
    const ultima = {}
    const enmiendas = {}
    ;(filas || []).filter((f) => !f.invalidada_at).forEach((f) => {
      if (esEnmiendaCasilla(f)) {
        if (!enmiendas[f.doc_key]) enmiendas[f.doc_key] = []
        enmiendas[f.doc_key].push(f)
      } else if (!ultima[f.doc_key]) {
        ultima[f.doc_key] = f
      }
    })
    const invalidadas = {}
    ;(filas || []).filter((f) => f.invalidada_at)
      .forEach((f) => { if (!invalidadas[f.doc_key]) invalidadas[f.doc_key] = f })

    const data = REQUIRED_ORG_LEGAL_DOCS.map((docKey) => {
      const def = getLegalDocDefinition(docKey)
      const vigente = def ? def.version : ''
      const acc = ultima[docKey]
      const persona = acc?.accepted_by ? porId[acc.accepted_by] : null
      return {
        docKey,
        titulo: def?.titulo || docKey,
        versionVigente: vigente,
        versionAceptada: acc?.doc_version || null,
        alDia: !!acc && acc.doc_version === vigente,
        aceptadoPor: persona?.nombre_completo || persona?.email || null,
        aceptadoEl: acc?.accepted_at || acc?.created_at || null,
        invalidada: invalidadas[docKey]
          ? {
              motivo: invalidadas[docKey].invalidada_motivo,
              quien: porId[invalidadas[docKey].accepted_by]?.nombre_completo
                || porId[invalidadas[docKey].accepted_by]?.email || null,
            }
          : null,
        // Enmiendas por casilla (la más reciente primero): qué cláusula agregó cada una y quién.
        // `clausula` es null en las anteriores a `clausula_nueva`.
        enmiendas: (enmiendas[docKey] || []).map((f) => ({
          clausula: f.documento_datos?.clausula_nueva || null,
          fecha: f.accepted_at || f.created_at || null,
          quien: nombreDe(f.accepted_by),
        })),
      }
    })

    return { data, error: null }
  } catch (error) {
    console.error('[getExpedienteLegal]', error)
    return { data: [], error }
  }
}

// Documentos legales que la org (su admin) AÚN debe aceptar para activarse.
// Devuelve { pendientes: [{docKey, version, doc}], yaActivada, puedeFirmar, error }.
// `doc` viene renderizado con la entidad legal de MYKLUBB, listo para mostrar en la puerta.
// Desde la pieza 2b el DPA es el mismo para todos los clubes (lo que se comparte en torneos es
// fijo): no se leen casillas. Las enmiendas viejas por casilla no cuentan como aceptacion.
export const getOrgLegalPendientes = async (orgId = null) => {
  try {
    const { data: staff } = (await getCurrentStaff()) || {}
    const resolvedOrgId = getActiveOrgOverride() || orgId || staff?.org_id || DEFAULT_ORG_ID
    if (!resolvedOrgId) return { pendientes: [], yaActivada: false, puedeFirmar: false, error: null }

    const { data: legal } = await getPlatformLegalEntity()

    // ¿Quien entra puede firmar por esta org? El contrato lo firma el TITULAR de la cuenta,
    // no cualquier admin -- RAIDERS tiene cuatro, y cualquiera de ellos podía obligar al
    // club -- y, si la org no tiene fundador activo, un admin. Lo responde la base
    // (_puede_firmar_por_org), la misma regla que revisa el alta de la aceptación. Si la
    // consulta falla, nadie acepta: ante la duda sobre una frontera legal, cerrar.
    const { data: firma, error: errFirma } = await supabase.rpc('_puede_firmar_por_org', { p_org: resolvedOrgId })
    const puedeFirmar = !errFirma && firma === true

    // Trae todas las aceptaciones de la org de una vez. Las enmiendas viejas por casilla se
    // descartan: no son la aceptación del titular.
    const { data: filas, error } = await supabase
      .from('org_legal_acceptances')
      .select('doc_key, doc_version, documento_datos')
      .eq('org_id', resolvedOrgId)
      .is('invalidada_at', null)
    if (error) throw error
    const aceptadas = (filas || []).filter((a) => !esEnmiendaCasilla(a))

    const aceptado = new Set(aceptadas.map((a) => `${a.doc_key}@${a.doc_version}`))
    const aceptadoAlgunaVez = new Set(aceptadas.map((a) => a.doc_key))

    const pendientes = REQUIRED_ORG_LEGAL_DOCS.map((docKey) => {
      const def = getLegalDocDefinition(docKey)
      const version = def ? def.version : ''
      const ya = aceptado.has(`${docKey}@${version}`)
      return ya ? null : { docKey, version, doc: renderDocRequerido(docKey, legal || {}) }
    }).filter(Boolean)

    // ¿La org ya se había activado alguna vez, con una versión anterior?
    //
    // Distingue dos situaciones que la puerta trataba igual y no lo son: una org
    // NUEVA que todavía no arranca, y una que lleva meses operando y a la que solo
    // le subió la versión de un documento. En la segunda no se puede encerrar a
    // todo el staff -- un coach que va a pasar lista no puede quedar bloqueado por
    // un contrato que ni siquiera está facultado para aceptar.
    const yaActivada = REQUIRED_ORG_LEGAL_DOCS.every((docKey) => aceptadoAlgunaVez.has(docKey))

    return { pendientes, yaActivada, puedeFirmar, error: null }
  } catch (error) {
    console.error('[getOrgLegalPendientes]', error)
    return { pendientes: [], yaActivada: true, puedeFirmar: false, error }
  }
}
